import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import axios from 'axios';
import type {
  CompatibilityResult,
  ConflictReport,
  PushPlan,
  PushResult,
  ResolveBody,
  ValidationIssue,
  ValidationResult,
} from '@klingerator/shared';
import { aiService } from '@/services/aiService';
import { pushResultFromError, syncService } from '@/services/syncService';
import { extractErrorMessage } from '@/services/api';
import { toastService } from '@/services/ToastService';
import { useProductEditorStore } from './productEditor';
import { useProductTreeStore } from './productTree';
import { useProductsStore } from './products';

/** Foutcodes die de view naar een tab/actie moet vertalen. */
export type SyncErrorCode =
  | 'VALIDATION_FAILED'
  | 'UNRESOLVED_CONFLICT'
  | 'NO_TEMPLATE'
  | 'PUSH_PROD_ADMIN_ONLY'
  | 'CHOICES_REQUIRED'
  | 'UNIT4_PRODUCT_GONE'
  | 'OTHER';

export interface SyncError {
  code: SyncErrorCode;
  message: string;
  details?: unknown;
}

interface ProductSyncState {
  validation: ValidationResult | null;
  report: ConflictReport | null;
  reportLoaded: boolean;
  plan: PushPlan | null;
  result: PushResult | null;
  /** Laatste AI-controle "Aansluitingen controleren"; blijft staan als je van tabblad wisselt */
  compatibility: CompatibilityResult | null;
  compatibilityBusy: boolean;
  compatibilityError: string | null;
}

function emptyState(): ProductSyncState {
  return {
    validation: null,
    report: null,
    reportLoaded: false,
    plan: null,
    result: null,
    compatibility: null,
    compatibilityBusy: false,
    compatibilityError: null,
  };
}

function classify(e: unknown): SyncError {
  if (axios.isAxiosError(e)) {
    const err = (e.response?.data as any)?.error;
    const code = err?.code as string | undefined;
    const known: SyncErrorCode[] = [
      'VALIDATION_FAILED',
      'UNRESOLVED_CONFLICT',
      'NO_TEMPLATE',
      'PUSH_PROD_ADMIN_ONLY',
      'CHOICES_REQUIRED',
      'UNIT4_PRODUCT_GONE',
    ];
    return {
      code: known.includes(code as SyncErrorCode) ? (code as SyncErrorCode) : 'OTHER',
      message: extractErrorMessage(e),
      details: err?.details,
    };
  }
  return { code: 'OTHER', message: extractErrorMessage(e) };
}

/**
 * Sync-store: per product de laatste validatie, het conflictrapport en het laatste push-plan/-resultaat.
 * Na push/refresh/resolve wordt de productEditor herladen (server-staat is dan leidend).
 * Fouten met een bekende code worden als `SyncError` teruggegeven via `lastError` én gegooid,
 * zodat de view kan navigeren (VALIDATION_FAILED → tab Validatie, UNRESOLVED_CONFLICT → tab Conflicten).
 */
export const useSyncStore = defineStore('sync', () => {
  const states = reactive(new Map<number, ProductSyncState>());
  const busy = reactive({
    validate: false,
    dryRun: false,
    push: false,
    resume: false,
    refresh: false,
    conflicts: false,
    resolve: false,
  });
  const lastError = ref<SyncError | null>(null);

  const anyBusy = computed(() => Object.values(busy).some(Boolean));

  function state(id: number): ProductSyncState {
    if (!states.has(id)) states.set(id, emptyState());
    return states.get(id)!;
  }
  const validation = (id: number) => state(id).validation;
  const report = (id: number) => state(id).report;
  const plan = (id: number) => state(id).plan;
  const result = (id: number) => state(id).result;
  const compatibility = (id: number) => state(id).compatibility;
  const compatibilityBusy = (id: number) => state(id).compatibilityBusy;
  const compatibilityError = (id: number) => state(id).compatibilityError;
  /**
   * AI-controle van de aansluitingen. Gooit nooit: de fout staat per product in de state, zodat een
   * mislukte controle de validatie ernaast niet raakt. `auto` = meegenomen met Valideren: een product met
   * te weinig regels is dan geen fout (niemand vroeg om deze controle).
   */
  async function checkCompatibility(id: number, opts: { auto?: boolean } = {}) {
    const s = state(id);
    if (s.compatibilityBusy) return;
    s.compatibilityBusy = true;
    s.compatibilityError = null;
    try {
      s.compatibility = await aiService.checkCompatibility(id);
    } catch (e) {
      const tooFew =
        axios.isAxiosError(e) && (e.response?.data as any)?.error?.code === 'COMPATIBILITY_TOO_FEW_LINES';
      if (opts.auto && tooFew) s.compatibility = null;
      else s.compatibilityError = extractErrorMessage(e, 'De AI-controle is mislukt');
    } finally {
      s.compatibilityBusy = false;
    }
  }

  /** Herlaadt editor + boom + lijstcache na een server-side mutatie. */
  async function reloadProduct(id: number) {
    const editor = useProductEditorStore();
    const tree = useProductTreeStore();
    const products = useProductsStore();
    if (editor.productId === id) {
      await editor.load(id, { silent: true });
      if (editor.product) products.remember(editor.product);
    } else {
      products.byId.delete(id);
    }
    tree.invalidate();
  }

  /** Acties die veranderen wélke producten in de lijst staan (dan de hele lijst herladen). */
  const MEMBERSHIP_ACTIONS = new Set(['create', 'archive', 'restore', 'set_root']);
  /** Laatste wijziging van buitenaf (chat-assistent); de productpagina reageert erop (boom, sync-log, badge). */
  const lastExternalChange = ref<{ id: number; action: string; seq: number } | null>(null);

  /**
   * Server-side wijziging van buiten de pagina om (schrijftool van de chat-assistent): verouderde
   * validatie/plan/rapport weggooien, het geopende product herladen (tenzij er niet-opgeslagen
   * wijzigingen zijn), lijstrij/boom/naamcache bijwerken — hetzelfde als na de knoppen op de pagina.
   * Geeft terug wat er met de editor gebeurde, zodat de aanroeper een melding kan tonen.
   */
  async function externalChange(id: number, action: string): Promise<'reloaded' | 'dirty' | 'not_open'> {
    const editor = useProductEditorStore();
    const tree = useProductTreeStore();
    const products = useProductsStore();
    states.delete(id);
    tree.invalidate();
    if (action === 'archive') products.forget(id);
    else products.byId.delete(id);
    let outcome: 'reloaded' | 'dirty' | 'not_open' = 'not_open';
    if (editor.productId === id) {
      if (editor.dirty) outcome = 'dirty';
      else {
        await editor.load(id, { silent: true });
        if (editor.product) products.remember(editor.product);
        outcome = 'reloaded';
      }
    }
    if (MEMBERSHIP_ACTIONS.has(action)) products.invalidate();
    else if (outcome === 'not_open') await products.refreshItem(id);
    lastExternalChange.value = { id, action, seq: (lastExternalChange.value?.seq ?? 0) + 1 };
    return outcome;
  }

  function fail(e: unknown): never {
    const err = classify(e);
    lastError.value = err;
    throw err;
  }

  /** Validatie-issues uit een 422 VALIDATION_FAILED in de state stoppen (zodat de tab Validatie ze toont). */
  function applyIssuesFromError(id: number, err: SyncError) {
    const issues = (err.details as any)?.issues as ValidationIssue[] | undefined;
    if (!Array.isArray(issues)) return;
    const s = state(id);
    s.validation = {
      ok: false,
      productId: id,
      issues,
      errorCount: issues.filter((i) => i.severity === 'error').length,
      warningCount: issues.filter((i) => i.severity === 'warning').length,
      order: s.validation?.order ?? [id],
      validatedAt: new Date().toISOString(),
    };
  }

  async function validate(id: number): Promise<ValidationResult> {
    busy.validate = true;
    lastError.value = null;
    const toastId = toastService.loading('Valideren… (controleert ook Unit4, kan tot 30 s duren)');
    try {
      const res = await syncService.validate(id);
      state(id).validation = res;
      if (res.ok && !res.warningCount)
        toastService.finish(toastId, 'Validatie geslaagd — geen fouten', 'bg-success');
      else if (res.ok)
        toastService.finish(
          toastId,
          `Validatie geslaagd met ${res.warningCount} waarschuwing${res.warningCount === 1 ? '' : 'en'}`,
          'bg-warning',
        );
      else
        toastService.finish(
          toastId,
          `Validatie: ${res.errorCount} fout${res.errorCount === 1 ? '' : 'en'}`,
          'bg-danger',
        );
      // validation_errors op product/regels zijn server-side bijgewerkt
      const editor = useProductEditorStore();
      if (editor.productId === id) await editor.refreshOriginal().catch(() => undefined);
      return res;
    } catch (e) {
      toastService.finish(toastId, extractErrorMessage(e, 'Valideren mislukt'), 'bg-danger');
      fail(e);
    } finally {
      busy.validate = false;
    }
  }

  async function dryRun(id: number): Promise<PushPlan> {
    busy.dryRun = true;
    lastError.value = null;
    const toastId = toastService.loading('Dry-run wordt voorbereid…');
    try {
      const p = await syncService.dryRun(id);
      state(id).plan = p;
      state(id).result = null;
      toastService.finish(
        toastId,
        `Dry-run klaar: ${p.operations.length} operatie${p.operations.length === 1 ? '' : 's'}`,
        'bg-info',
      );
      return p;
    } catch (e) {
      const err = classify(e);
      toastService.finish(toastId, err.message, 'bg-danger');
      if (err.code === 'VALIDATION_FAILED') applyIssuesFromError(id, err);
      fail(e);
    } finally {
      busy.dryRun = false;
    }
  }

  async function handlePushOutcome(id: number, res: PushResult, toastId: number, verb: string) {
    const s = state(id);
    s.plan = res.plan;
    s.result = res;
    if (res.ok) {
      // snapshot is bijgewerkt → oud conflictrapport/validatie zijn niet meer relevant
      s.report = null;
      s.reportLoaded = false;
      s.validation = null;
      toastService.finish(toastId, res.message || `${verb} geslaagd`, 'bg-success');
    } else {
      toastService.finish(
        toastId,
        res.message || `${verb} gedeeltelijk mislukt — bekijk de operaties en hervat`,
        'bg-danger',
        true,
        10_000,
      );
    }
    await reloadProduct(id);
    return res;
  }

  async function push(id: number): Promise<PushResult> {
    busy.push = true;
    lastError.value = null;
    const toastId = toastService.loading('Opslaan in Unit4…');
    try {
      const res = await syncService.push(id);
      return await handlePushOutcome(id, res, toastId, 'Opslaan in Unit4');
    } catch (e) {
      const partial = pushResultFromError(e);
      if (partial) return await handlePushOutcome(id, partial, toastId, 'Opslaan in Unit4');
      const err = classify(e);
      toastService.finish(toastId, err.message, 'bg-danger');
      if (err.code === 'VALIDATION_FAILED') applyIssuesFromError(id, err);
      fail(e);
    } finally {
      busy.push = false;
    }
  }

  async function resume(id: number): Promise<PushResult> {
    busy.resume = true;
    lastError.value = null;
    const toastId = toastService.loading('Hervatten van de synchronisatie…');
    try {
      const res = await syncService.resume(id);
      return await handlePushOutcome(id, res, toastId, 'Hervatten');
    } catch (e) {
      const partial = pushResultFromError(e);
      if (partial) return await handlePushOutcome(id, partial, toastId, 'Hervatten');
      toastService.finish(toastId, extractErrorMessage(e, 'Hervatten mislukt'), 'bg-danger');
      fail(e);
    } finally {
      busy.resume = false;
    }
  }

  async function refresh(id: number): Promise<{ conflict: boolean; report: ConflictReport | null }> {
    busy.refresh = true;
    lastError.value = null;
    const toastId = toastService.loading('Vernieuwen uit Unit4…');
    try {
      const res = await syncService.refresh(id);
      const s = state(id);
      s.report = res.report;
      s.reportLoaded = true;
      if (res.conflict)
        toastService.finish(
          toastId,
          'Unit4 wijkt af van de lokale versie — los het conflict op',
          'bg-warning',
          true,
          8000,
        );
      else toastService.finish(toastId, 'Product is bijgewerkt vanuit Unit4', 'bg-success');
      await reloadProduct(id);
      return { conflict: res.conflict, report: res.report };
    } catch (e) {
      toastService.finish(toastId, extractErrorMessage(e, 'Vernieuwen mislukt'), 'bg-danger');
      fail(e);
    } finally {
      busy.refresh = false;
    }
  }

  async function loadConflicts(id: number, force = false): Promise<ConflictReport | null> {
    const s = state(id);
    if (s.reportLoaded && !force) return s.report;
    busy.conflicts = true;
    try {
      s.report = await syncService.conflicts(id);
      s.reportLoaded = true;
      return s.report;
    } catch (e) {
      fail(e);
    } finally {
      busy.conflicts = false;
    }
  }

  async function resolve(id: number, body: ResolveBody): Promise<void> {
    busy.resolve = true;
    lastError.value = null;
    try {
      await syncService.resolve(id, body);
      const s = state(id);
      s.report = null;
      s.reportLoaded = true;
      s.validation = null;
      s.compatibility = null; // regels kunnen veranderd zijn
      toastService.success('Conflict opgelost — de gekozen versie is toegepast');
      await reloadProduct(id);
    } catch (e) {
      const err = classify(e);
      toastService.error(err.message);
      fail(e);
    } finally {
      busy.resolve = false;
    }
  }

  function clear(id?: number) {
    if (id === undefined) states.clear();
    else states.delete(id);
    lastError.value = null;
  }

  return {
    states,
    busy,
    anyBusy,
    lastError,
    validation,
    report,
    plan,
    result,
    compatibility,
    compatibilityBusy,
    compatibilityError,
    checkCompatibility,
    validate,
    dryRun,
    push,
    resume,
    refresh,
    loadConflicts,
    resolve,
    clear,
    lastExternalChange,
    externalChange,
  };
});
